"use client";
import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { ComponentProps, FC, Fragment, useEffect, useState } from "react";
import { Button } from "../Button";
import { XMarkThin } from "@/icons";
import { Analytics } from "@/types";
import { RootFilters } from "./RootFilters";
import { useFilters } from "@/hooks";
import { Filters, TreeNode } from "@/types/General";

type VerticalFilterProps = {
  variant: "standard" | "button-trigger";
  defaultOpen?: boolean;
  sortByName?: boolean;
  name?: string;
  analytics: Analytics;
  clearAll: { text: string } & Analytics;
  selectAll: { text: string } & Analytics;
  deselectAll: { text: string } & Analytics;
  applyFilter: {
    text: string;
  } & Analytics;
  filters: {
    label: string;
    name: string;
    categories: TreeNode[];
  }[];
  selectedFilters: { name: string; paths: string[] }[];
  onFilter: (filters: Filters) => void;
} & ComponentProps<"div">;

type BaseFilterProps = {
  defaultOpen?: boolean;
  sortByName?: boolean;
  name?: string;
  analytics: Analytics;
  clearAll: { text: string } & Analytics;
  selectAll: { text: string } & Analytics;
  deselectAll: { text: string } & Analytics;
  applyFilter: {
    text: string;
  } & Analytics;
  filters: {
    label: string;
    name: string;
    categories: TreeNode[];
  }[];
  selectedFilters: Filters;
  onFilter: (filters: Filters) => void;
} & ComponentProps<"div">;

const StandardVerticalFilter: FC<BaseFilterProps> = ({
  defaultOpen = false,
  sortByName = true,
  name,
  analytics,
  applyFilter,
  filters,
  clearAll,
  selectAll,
  deselectAll,
  selectedFilters,
  onFilter,
  ...rest
}) => {
  const {
    selectedFilters: internalSelectedFilters,
    onSelectFilters,
    onDeselectFilters,
    setSelectedFilters,
  } = useFilters(selectedFilters);

  useEffect(() => {
    setSelectedFilters(selectedFilters);
  }, [selectedFilters, setSelectedFilters]);

  return (
    <div {...rest}>
      <div className="flex flex-col gap-3">
        {name && (
          <div className="text-subhead-2xs text-body-secondary font-bold uppercase">
            {name}
          </div>
        )}
        <div className="bg-background-highlight pt-3 pr-2 pb-6 pl-6">
          <RootFilters
            defaultOpen={defaultOpen}
            sortByName={sortByName}
            analytics={analytics}
            selectAll={selectAll}
            deselectAll={deselectAll}
            selectedFilters={internalSelectedFilters}
            filters={filters}
            onSelectFilters={(filterName, paths) => {
              const newFilters = onSelectFilters(filterName, paths);
              onFilter(newFilters);
            }}
            onDeselectFilters={(filterName, paths) => {
              const newFilters = onDeselectFilters(filterName, paths);
              onFilter(newFilters);
            }}
          />
        </div>
      </div>
    </div>
  );
};

const ButtonTriggeredVerticalFilter: FC<BaseFilterProps> = ({
  defaultOpen = false,
  sortByName = true,
  name,
  analytics,
  applyFilter,
  clearAll,
  selectAll,
  deselectAll,
  selectedFilters,
  filters,
  onFilter,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    selectedFilters: internalSelectedFilters,
    onSelectFilters,
    onDeselectFilters,
    onClearAll,
    setSelectedFilters,
  } = useFilters(selectedFilters);

  useEffect(() => {
    setSelectedFilters(selectedFilters);
  }, [selectedFilters, setSelectedFilters]);

  return (
    <div {...rest}>
      <Button
        variant="index"
        icon="filter"
        onClick={() => setIsOpen((prevValue) => !prevValue)}
      >
        {name}
      </Button>
      <Transition show={isOpen}>
        <Dialog onClose={() => setIsOpen(false)} className="relative">
          <div className="bg-swdc-black/40 fixed inset-0 z-50 w-screen">
            <DialogBackdrop
              className="fixed inset-0"
              onClick={() => setIsOpen(false)}
            />
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <DialogPanel className="bg-background-highlight absolute right-0 flex size-full h-full w-100 max-w-full flex-col">
                <div className="relative h-full overflow-y-auto pt-14 pr-2 pl-6">
                  <CloseButton
                    as={XMarkThin}
                    className="absolute top-3 right-6 hover:cursor-pointer"
                    aria-label="Close"
                  />
                  <div className="flex h-full flex-col gap-3">
                    <div className="flex flex-row items-center justify-between pr-6">
                      {name && (
                        <div className="text-subhead-2xs text-body-secondary font-bold uppercase">
                          {name}
                        </div>
                      )}
                      {selectedFilters.length > 0 && (
                        <Button
                          variant="tertiary"
                          icon="none"
                          onClick={onClearAll}
                          className="text-left capitalize underline hover:no-underline!"
                        >
                          {clearAll.text}
                        </Button>
                      )}
                    </div>
                    <RootFilters
                      defaultOpen={defaultOpen}
                      sortByName={sortByName}
                      analytics={analytics}
                      selectAll={selectAll}
                      deselectAll={deselectAll}
                      selectedFilters={internalSelectedFilters}
                      filters={filters}
                      onSelectFilters={onSelectFilters}
                      onDeselectFilters={onDeselectFilters}
                    />
                  </div>
                </div>
                <div className="p-6 pt-0">
                  <div className="border-t-solid border-t-swdc-silver-300 flex w-full border-t-1 pt-6">
                    <Button
                      variant="primary"
                      className="mx-auto"
                      onClick={() => {
                        onFilter(internalSelectedFilters);
                        setIsOpen(false);
                      }}
                    >
                      {applyFilter.text}
                    </Button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export const VerticalFilter: FC<VerticalFilterProps> = (props) => {
  switch (props.variant) {
    case "standard":
      return <StandardVerticalFilter {...props} />;
    case "button-trigger":
      return <ButtonTriggeredVerticalFilter {...props} />;
    default:
      return <StandardVerticalFilter {...props} />;
  }
};
